import { type AxiosInstance } from "axios";
import { LatLng } from "leaflet";
import { useCallback } from "react";
import { toast } from "react-toastify";
import { SALE_DOCUMENTS_URL } from "@/features/reassistSale/api/useReassistSale";
import {
  type SaleDocumentListResponse,
  type SaleDocumentSummary,
} from "@/features/reassistSale/types";
import { displayName } from "@/features/reassistSale/utils/format";
import { MUNICIPAL_DOCUMENT_LAYER_NAME } from "../components/layers/MunicipalDocumentLayer";
import { addMunicipalDocumentsLayer } from "../utils/urlParams/applyLayerParams";

/** 住居表示の検索語で役所資料（黒塗り）の物件を探すときの件数。 */
export const SALE_DOCUMENT_SEARCH_LIMIT = 5;

/** 地図を動かせる（座標のある）役所資料の物件。 */
export interface SaleDocumentSearchHit {
  lat: number;
  lng: number;
  /** 案内に出す名前（マンション名。無ければ住所）。 */
  label: string;
  /** 先頭以外に座標のある物件の件数。 */
  otherCount: number;
}

/**
 * 検索結果から、座標のある先頭の物件を選ぶ。座標が無い（null・数値にならない）物件は数えない。
 * @param results - 検索結果
 * @returns 先頭の物件。座標のある物件が無ければ null
 */
export const toSaleDocumentSearchHit = (
  results: SaleDocumentSummary[]
): SaleDocumentSearchHit | null => {
  const located = results
    .map((document) => ({
      document,
      lat: Number.parseFloat(document.latitude ?? ""),
      lng: Number.parseFloat(document.longitude ?? ""),
    }))
    .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng));
  const first = located.at(0);
  if (first === undefined) return null;
  return {
    lat: first.lat,
    lng: first.lng,
    label: displayName(first.document),
    otherCount: located.length - 1,
  };
};

/**
 * 見つかったときの案内。
 * @param hit - 見つかった物件
 * @returns 例「役所資料（黒塗り）の物件が見つかりました: バージュアル横浜鶴見（ほか1件）」
 */
export const saleDocumentFoundMessage = (hit: SaleDocumentSearchHit): string =>
  `役所資料（黒塗り）の物件が見つかりました: ${hit.label}${
    hit.otherCount > 0 ? `（ほか${hit.otherCount}件）` : ""
  }`;

interface Params {
  apiClient: AxiosInstance;
  /** 地図の表示位置を変える（住所検索と同じ方法で地図を動かす）。 */
  setViewLatLng: (latlng: LatLng) => void;
  setCheckedLayers: (update: (prev: string[]) => string[]) => void;
  /**
   * 地図（LeafletMap）が持つレイヤーの表示状態を切り替える。checkedLayers の更新だけでは、
   * 地図側の表示状態（overlayVisibility）に届かないため、こちらも呼ぶ。
   */
  setOverlayVisible: (name: string, visible: boolean) => void;
  /** false のとき（reassist-archive フラグがオフ）は、物件を探さず、レイヤーもオンにしない。 */
  enabled?: boolean;
}

/**
 * 住居表示の検索で住所が見つからなかったとき、同じ検索語（マンション名など）で役所資料（黒塗り）の物件を探す。
 * BE はマンション名・住所・地番・家屋番号を、異体字（横濱=横浜など）を同じ字として探す。
 * 見つかれば先頭の物件へ地図を動かし、「役所資料（黒塗り）」のレイヤーをオンにして案内する。
 * 住所が見つかったときも、その場所の役所資料のピンが見えるよう、レイヤーをオンにする（案内は出さない）。
 * @returns findAndShow - 見つけて地図を動かしたら true。見つからない・取得に失敗したら false（呼び出し側が従来の「見つからない」を出す）
 * @returns showMunicipalDocumentLayer - 「役所資料（黒塗り）」のレイヤーをオンにする
 */
export const useSaleDocumentSearchFallback = ({
  apiClient,
  setViewLatLng,
  setCheckedLayers,
  setOverlayVisible,
  enabled = true,
}: Params): {
  findAndShow: (keyword: string) => Promise<boolean>;
  showMunicipalDocumentLayer: () => void;
} => {
  /** 「役所資料（黒塗り）」のレイヤーをオンにする。既にオンなら同じ配列を返す（再描画させない）。 */
  const showMunicipalDocumentLayer = useCallback(() => {
    if (!enabled) return;
    setCheckedLayers(addMunicipalDocumentsLayer);
    setOverlayVisible(MUNICIPAL_DOCUMENT_LAYER_NAME, true);
  }, [enabled, setCheckedLayers, setOverlayVisible]);

  const findAndShow = useCallback(
    async (keyword: string): Promise<boolean> => {
      const q = keyword.trim();
      if (!enabled || q === "") return false;
      let hit: SaleDocumentSearchHit | null;
      try {
        const response = await apiClient.get<SaleDocumentListResponse>(
          SALE_DOCUMENTS_URL,
          // 座標のある物件だけを返させる（座標の無い物件が先頭を占めて、移動できる物件を見落とさないため）
          { params: { q, limit: SALE_DOCUMENT_SEARCH_LIMIT, located: true } }
        );
        hit = toSaleDocumentSearchHit(response.data.results);
      } catch {
        // 役所資料の検索の失敗は伝えない（呼び出し側の「見つからない」の案内に任せる）
        return false;
      }
      if (hit === null) return false;
      setViewLatLng(new LatLng(hit.lat, hit.lng));
      showMunicipalDocumentLayer();
      toast.info(saleDocumentFoundMessage(hit));
      return true;
    },
    [apiClient, enabled, setViewLatLng, showMunicipalDocumentLayer]
  );
  return { findAndShow, showMunicipalDocumentLayer };
};
